import { useState } from "react";
import api, { formatApiError, money, fmtDate } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Upload, CheckCircle2, FileSpreadsheet } from "lucide-react";

// Parses CSV: expected columns "data,valor,descricao" or "date,amount,description"
// Accepts BR format: DD/MM/YYYY and comma decimals
function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (!lines.length) return [];
  const header = lines[0].toLowerCase().split(/[,;]/).map(h => h.trim());
  const idxDate = header.findIndex(h => /data|date/.test(h));
  const idxVal = header.findIndex(h => /valor|amount|value/.test(h));
  const idxDesc = header.findIndex(h => /descri|descr|memo|obs|hist/.test(h));
  if (idxDate < 0 || idxVal < 0) return null;
  const out = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(/[,;]/).map(c => c.trim().replace(/^"|"$/g, ""));
    if (!cells[idxDate] || !cells[idxVal]) continue;
    let d = cells[idxDate];
    if (d.includes("/")) { const [dd, mm, yy] = d.split("/"); d = `${yy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`; }
    let v = cells[idxVal].replace(/[^0-9,.\-]/g, "");
    if (v.includes(",") && v.includes(".")) v = v.replace(/\./g, "").replace(",", ".");
    else if (v.includes(",")) v = v.replace(",", ".");
    out.push({ data: d, valor: parseFloat(v) || 0, descricao: idxDesc >= 0 ? (cells[idxDesc] || "") : "" });
  }
  return out;
}

export default function Reconciliacao() {
  const [lines, setLines] = useState([]);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState({});

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const parsed = parseCSV(text);
    if (!parsed) return toast.error("CSV precisa ter colunas 'data' e 'valor'");
    if (parsed.length === 0) return toast.error("Nenhuma linha válida");
    setLines(parsed);
    setResult(null); setSelected({});
    toast.success(`${parsed.length} linhas carregadas`);
  };

  const reconciliar = async () => {
    try {
      const { data } = await api.post("/bank/reconcile", { linhas: lines });
      setResult(data);
      const s = {}; data.matched.forEach(m => s[m.boleto_id] = true);
      setSelected(s);
      toast.success(`${data.matched.length} de ${lines.length} casadas`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmar = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) return toast.error("Selecione ao menos um match");
    try {
      const { data } = await api.post("/bank/reconcile/confirm", { boleto_ids: ids });
      toast.success(`${data.paid} boleto(s) baixado(s)`);
      setResult(null); setLines([]); setSelected({});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-3xl font-bold">Reconciliação Bancária</h1>
        <p className="text-slate-500 text-sm">Importe o extrato bancário (CSV) e case automaticamente com boletos em aberto</p>
      </div>

      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3"><FileSpreadsheet size={18} className="text-primary" /><div className="font-heading font-semibold">1. Importar CSV do extrato</div></div>
        <p className="text-xs text-slate-500 mb-3">Formato esperado: colunas <code className="bg-slate-100 px-1 rounded">data</code>, <code className="bg-slate-100 px-1 rounded">valor</code>, <code className="bg-slate-100 px-1 rounded">descricao</code> (opcional). Datas em DD/MM/AAAA ou AAAA-MM-DD. Valores com vírgula ou ponto.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Arquivo CSV</Label>
            <Input type="file" accept=".csv,text/csv" onChange={onFile} data-testid="rec-file" />
          </div>
          {lines.length > 0 && <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={reconciliar} data-testid="rec-run"><Upload size={16} /> Casar {lines.length} linhas</Button>}
        </div>
        {lines.length > 0 && !result && (
          <div className="mt-4 max-h-48 overflow-y-auto text-xs border border-slate-200 rounded-md">
            <table className="w-full">
              <thead className="bg-slate-50 text-left text-slate-600"><tr><th className="py-2 px-3">Data</th><th>Valor</th><th>Descrição</th></tr></thead>
              <tbody>{lines.map((l, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1.5 px-3">{fmtDate(l.data)}</td><td>{money(l.valor)}</td><td className="text-slate-500">{l.descricao}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </Card>

      {result && (
        <>
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /><div className="font-heading font-semibold">2. Casamentos encontrados ({result.matched.length})</div></div>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmar} data-testid="rec-confirm">Confirmar e baixar selecionados</Button>
            </div>
            {result.matched.length === 0 && <div className="text-slate-400 text-sm text-center py-6">Nenhuma linha do extrato bateu com boletos em aberto.</div>}
            <div className="space-y-2">
              {result.matched.map(m => (
                <div key={m.boleto_id} className="p-3 bg-emerald-50 border border-emerald-200 rounded-md">
                  <div className="flex flex-wrap justify-between gap-3 text-sm">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!selected[m.boleto_id]} onChange={e => setSelected({ ...selected, [m.boleto_id]: e.target.checked })} className="mt-1" data-testid={`sel-match-${m.numero_documento}`} />
                      <div>
                        <div className="font-medium text-slate-900">Boleto <b>{m.numero_documento}</b> · {money(m.valor)}</div>
                        <div className="text-xs text-slate-600">Extrato: {fmtDate(m.line.data)} · {m.line.descricao || "—"} <Badge className="ml-2 bg-emerald-100 text-emerald-800">Δ {m.diff_dias}d</Badge></div>
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          {result.unmatched.length > 0 && (
            <Card className="p-5">
              <div className="font-heading font-semibold mb-3 text-amber-800">Sem correspondência ({result.unmatched.length})</div>
              <div className="space-y-2">
                {result.unmatched.map((u, i) => (
                  <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
                    <b>{fmtDate(u.line.data)} · {money(u.line.valor)}</b> — {u.line.descricao || "—"} <span className="text-xs text-amber-800 ml-2">({u.reason})</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
